import { useEffect, useRef } from "react";

interface UseSmartScrollOptions {
	threshold?: number;
}

/**
 * Keep auto-scroll enabled while the user stays near the bottom.
 * This is intended for live-updating feeds where new items append frequently.
 */
export function useSmartScroll<T extends HTMLElement>(
	itemCount: number,
	options: UseSmartScrollOptions = {},
) {
	const containerRef = useRef<T>(null);
	const shouldAutoScrollRef = useRef(true);
	const autoScrollThreshold = options.threshold ?? 100;
	const scrollEpsilon = 1;

	useEffect(() => {
		const container = containerRef.current;
		if (!container) {
			return;
		}

		const updateAutoScrollState = () => {
			const { scrollTop, scrollHeight, clientHeight } = container;
			const distanceToBottom = scrollHeight - scrollTop - clientHeight;
			shouldAutoScrollRef.current =
				distanceToBottom <= autoScrollThreshold + scrollEpsilon;
		};

		updateAutoScrollState();
		// Performance optimization: Mark scroll listener as passive to improve scrolling performance
		// by allowing the browser to optimize scrolling without waiting for the listener to complete.
		container.addEventListener("scroll", updateAutoScrollState, {
			passive: true,
		});

		return () => {
			container.removeEventListener("scroll", updateAutoScrollState);
		};
	}, [autoScrollThreshold]);

	useEffect(() => {
		const container = containerRef.current;
		if (container && itemCount > 0 && shouldAutoScrollRef.current) {
			container.scrollTop = container.scrollHeight;
		}
	}, [itemCount]);

	return containerRef;
}
